/* eslint-disable @typescript-eslint/ban-ts-comment */
import { Session } from '@inhouse-market/sdk';
import { message } from 'antd';
import axios from 'axios';
import { setUserId, setUserProperties } from 'firebase/analytics';
import { DateTime } from 'luxon';
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { analytics } from '../../services/firebase';

import apiSDK from '@/services/apiSdk';
import { EnRoles, listAdminRoles } from '../../interfaces/Roles';
import api from '../../services/api';
import useAssumeToken from '@/store/Sessions/useAssumeToken';
import queryClient, { queryKeys } from '@/store/queryClient';

interface IAuthContext {
    isAuth: boolean;
    logIn: (email: string, password: string, recaptcha: string) => Promise<void>;
    logOut: () => void;
    data: Data;
    getAssumedToken: (id: number) => Promise<void>;
    resetAssumedToken: () => void;
    isAssuming: boolean;
    isAdmin: boolean;
    isLoading: boolean;
}

export interface ShopManager {
    id: number;
    user_id: number;
    is_blocked: number;
    name: string;
    type_pix: string | null;
    pix: string | null;
    doc: string;
    type_doc: 'cnpj' | 'cpf' | string;
    phone: string;
    created_at: string;
    updated_at: string;
    trading_name: string;
    municipal_inscription: string | null;
    state_inscription: string | null;
    digital_certificate_password: string | null;
    tax_regime: string | null;
    address_id: number;
    iugu_live_api_token: string | null;
    iugu_test_api_token: string | null;
    iugu_user_token: string | null;
    focus_id: number | null;
    percentage: string;
    id_token_nfce_production: number | null;
    emit_invoice: number;
    csc_nfce_production: string | null;
    current_reference: number;
    verified_iugu_account: number | null;
    verified_shopmanager: number;
    recipient_id: string;
    antecipation: number;
    active_pwa: number;
    active_app: number;
    active_pagarme: 'active' | 'inactive' | string;
    active_fraud_prevention: number;
    use_plugpag: number;
    plugpag_email: string | null;
    plugpag_token: string | null;
    pagbank_split_percentage: string;
    monthly_fee_type: 'MINIMUM' | 'STANDARD' | string;
    monthly_fee_value: string;
    focus_nfe_token: string | null;
    focus_webhook_id: string | null;
    accounting_cnpj: string | null;
    focus_global_token: string | null;
    can_request_antecipation: number;
    annual_revenue: number;
    contact_email: string | null;
    phone2: string | null;
    licenses_quantity: number;
    licenses_quantity_totem: number;
}

type User = {
    id?: number;
    email?: string;
    is_confirmed?: boolean;
    shopManager?: ShopManager;
    created_at?: DateTime;
    updated_at?: DateTime;
};

export type Data = {
    user: Session.SigninResponse["user"] | null;
    role: Session.SigninResponse["roles"] | [];
    error?: { reason: string; date: string };
};

export type Role = {
    type: EnRoles;
};

export const authContext = createContext<IAuthContext>({} as IAuthContext);

const AuthContext: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isAuth, setIsAuth] = useState(false);
    const [isAssuming, setIsAssuming] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const logoutInProgressRef = useRef(false);

    const dataInitialState = {
        user: null,
        role: [],
    };

    const [data, setData] = useState<Data>(dataInitialState);
    const { mutateAsync: performAssumeToken } = useAssumeToken()

    const logIn = useCallback(async (email: string, password: string, reCaptcha: string) => {
        try {
            setIsLoading(true);
            const response = await apiSDK.session.login({
                email,
                password,
                reCaptcha,
            })

            const { user, roles } = response.data;

            if (
                roles.every(
                    (role: { type: string }) =>
                        role.type === EnRoles.trustee ||
                        role.type === EnRoles.restocker ||
                        role.type === EnRoles.customer,
                )
            ) {
                throw new Error('Usuário inválido para o acesso');
            }

            const { token } = response.data.token;

            localStorage.setItem('@NeerHouse:token', token);
            localStorage.setItem('@NeerHouse:user', JSON.stringify(user));
            localStorage.setItem('@NeerHouse:userId', user.id.toString());
            setData({ user, role: roles });
            // tell to analytics that user is logged in
            setUserId(analytics, user.id.toString());
            setUserProperties(analytics, { ...user });
            queryClient.invalidateQueries({ queryKey: queryKeys.GET_ME });
            setIsAuth(true);
        } catch (error: any) {
            if (
                axios.isAxiosError(error) &&
                error?.response?.data?.error === 'bloqued-account'
            ) {
                message.error('Sua conta está bloqueada');
            }
            if (
                error?.response &&
                error?.response.data.message &&
                error?.response.data.message ===
                'E_INVALID_AUTH_PASSWORD: Password mis-match'
            ) {
                message.error(
                    'Ocorreu um erro, verifique suas credenciais e tente novamente',
                );
            } else {
                if (error?.response && error?.response?.data?.message) {
                    setData((oldData) => {
                        return {
                            ...oldData,
                            error: error?.response?.data,
                        };
                    });
                    return message.error(error?.response?.data?.message);
                }
                if (
                    error.message ===
                    "Cannot read properties of undefined (reading 'status')"
                ) {
                    return message.error('Erro de conexão');
                }
                if (error.message) {
                    return message.error(error.message);
                }
                message.error(
                    'Ocorreu um erro, verifique suas credenciais e tente novamente',
                );
            }
        } finally {
            setIsLoading(false);
        }
    }, []);

    const api_logout = async () => {
        if (logoutInProgressRef.current) return;
        logoutInProgressRef.current = true;
        try {
            setIsAuth(false);
            try {
                await api.get('/sessions/logout');
            } catch {
                // ignore error
            }
            localStorage.removeItem('@NeerHouse:token');
            localStorage.removeItem('@NeerHouse:user');
            localStorage.removeItem('@NeerHouse:userId');
            localStorage.removeItem('@NeerHouse:skipAnnouncementsUntil');
            resetAssumedToken();
        } finally {
            logoutInProgressRef.current = false;
        }
    };

    const logOut = useCallback(async () => {
        await api_logout();
    }, []);

    const getAssumedToken = useCallback(async (id: number) => {
        try {
            const response = await performAssumeToken(id)
            localStorage.setItem(
                '@NeerHouse:assumedToken',
                response.token,
            );
            setIsAssuming(true);
            queryClient.invalidateQueries({ queryKey: queryKeys.GET_ME });
        } catch (error: unknown) {
            logOut();
        }
    }, []);

    const resetAssumedToken = () => {
        localStorage.removeItem('@NeerHouse:assumedToken');
        setIsAssuming(false);
    };

    const verifyToken = () => {
        const token = localStorage.getItem('@NeerHouse:token');
        const assumedToken = localStorage.getItem('@NeerHouse:assumedToken');

        if (token && token !== null) {
            setIsAuth(true);
        }
        if (assumedToken && assumedToken !== null) {
            setIsAssuming(true);
        }
    };

    useEffect(() => {
        verifyToken();
    }, []);

    useEffect(() => {
        let cancelled = false;

        const loadUser = async () => {
            try {
                setIsLoading(true);
                if (isAuth) {
                    const response = await api.get('sessions/get-user', {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem('@NeerHouse:assumedToken') ??
                                localStorage.getItem('@NeerHouse:token')
                                }`,
                        },
                    });

                    if (cancelled) return;

                    setIsLoading(false);
                    const { user } = response.data;

                    if (isAssuming === false && user) {
                        setUserId(analytics, user.id.toString());
                        setUserProperties(analytics, user);
                    }
                    const role = response.data.roles;

                    setData({ user, role });
                } else {
                    if (cancelled) return;
                    setIsLoading(false);
                    setData(dataInitialState);
                }
            } catch (error) {
                if (cancelled) return;
                setIsLoading(false);
                if (axios.isAxiosError(error) && error?.response?.status === 401) {
                    setData(dataInitialState);
                    logOut();
                }
            }
        };

        loadUser();
        return () => {
            cancelled = true;
        };
    }, [isAuth, isAssuming, logOut]);

    const isAdmin = React.useMemo(() => {
        if (data?.role)
            return data.role.some((item: Role) =>
                listAdminRoles().includes(item.type),
            );
        return false;
    }, [data]);

    return (
        <authContext.Provider
            value={{
                isAuth,
                logIn,
                isAdmin,
                logOut,
                data,
                getAssumedToken,
                resetAssumedToken,
                isAssuming,
                isLoading,
            }}
        >
            {children}
        </authContext.Provider>
    );
};

function useAuth(): IAuthContext {
    return useContext(authContext);
}

export { useAuth };
export default AuthContext;
